import React, { useEffect, useState } from "react";
import { Save, Image as ImageIcon, Globe, Tag, ShieldAlert, MonitorPlay, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useUnsaved } from "../DashboardShell";

const CATEGORIES = [
  "Just Chatting", "IRL", "Music", "Art", "Cooking", "Tech", "Esports",
  "Tic-Tac-Toe", "Poker", "Chess",
];

const SOCIAL_KEYS = ["youtube", "twitter", "instagram", "tiktok", "discord", "twitch", "kick", "steam", "reddit", "facebook"];
const SOCIAL_LABELS = {
  youtube: "YouTube",  twitter: "X / Twitter",  instagram: "Instagram",
  tiktok: "TikTok",    discord: "Discord invite", twitch: "Twitch",
  kick: "Kick",        steam: "Steam profile",    reddit: "Reddit",
  facebook: "Facebook",
};

const LANGUAGES = ["English", "Spanish", "French", "German", "Portuguese", "Japanese", "Korean", "Chinese"];

export default function Channel() {
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);
  const [form, setForm] = useState({
    title: "", category: "Just Chatting", description: "", contest_rules: "",
    thumbnail_url: "", banner_url: "", avatar_url: "",
    tags: "", language: "English", mature: false,
    default_title: "", schedule: "",
    offline_screen_url: "", panels_markdown: "",
  });
  const [socials, setSocials] = useState({});
  const [initial, setInitial] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/channels/${user.username}`).then((r) => {
      setChannel(r.data);
      const next = {
        title: r.data.title || "",
        category: r.data.category || "Just Chatting",
        description: r.data.description || "",
        contest_rules: r.data.contest_rules || "",
        thumbnail_url: r.data.thumbnail_url || "",
        banner_url: r.data.banner_url || "",
        avatar_url: r.data.avatar_url || "",
        tags: Array.isArray(r.data.tags) ? r.data.tags.join(", ") : (r.data.tags || ""),
        language: r.data.language || "English",
        mature: !!r.data.mature,
        default_title: r.data.default_title || "",
        schedule: r.data.schedule || "",
        offline_screen_url: r.data.offline_screen_url || "",
        panels_markdown: r.data.panels_markdown || "",
      };
      setForm(next);
      setSocials(r.data.social_links || {});
      setInitial({ form: next, socials: r.data.social_links || {} });
    });
  }, [user?.username]);

  const dirty = initial && (JSON.stringify(form) !== JSON.stringify(initial.form) || JSON.stringify(socials) !== JSON.stringify(initial.socials));
  useUnsaved(dirty);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        social_links: socials,
      };
      await api.patch("/channels/me", payload);
      setInitial({ form, socials });
      toast.success("Channel updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = () => {
    if (!initial) return;
    setForm(initial.form);
    setSocials(initial.socials);
  };

  if (!channel) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="section-channel">
      {/* Branding */}
      <SectionBlock icon={ImageIcon} title="Branding" color="cyan">
        <FieldGrid>
          <Field label="Avatar URL">
            <input className="sc-input" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" data-testid="channel-avatar-url" />
          </Field>
          <Field label="Banner URL" hint="Recommended 1920×480">
            <input id="banner" className="sc-input" value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} placeholder="https://…" data-testid="channel-banner-url" />
          </Field>
          <Field label="Stream thumbnail" fullWidth>
            <input className="sc-input" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="https://…" data-testid="channel-thumbnail-url" />
          </Field>
          <Field label="Offline screen" hint="Shown when you're not streaming" fullWidth>
            <input className="sc-input" value={form.offline_screen_url} onChange={(e) => setForm({ ...form, offline_screen_url: e.target.value })} placeholder="https://…" data-testid="channel-offline-url" />
          </Field>
        </FieldGrid>
      </SectionBlock>

      {/* Stream defaults */}
      <SectionBlock icon={MonitorPlay} title="Stream Setup" color="volt">
        <FieldGrid>
          <Field label="Stream title">
            <input className="sc-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="channel-title" />
          </Field>
          <Field label="Default title" hint="Auto-fills when you go live">
            <input className="sc-input" value={form.default_title} onChange={(e) => setForm({ ...form, default_title: e.target.value })} data-testid="channel-default-title" />
          </Field>
          <Field label="Category">
            <select className="sc-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="channel-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Language">
            <select className="sc-input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="channel-language">
              {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
            </select>
          </Field>
          <Field label="Tags" hint="Comma-separated, e.g. 'chill, competitive'" fullWidth>
            <input className="sc-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="channel-tags" />
          </Field>
          <Field label={<span className="flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5" /> Mature content</span>} fullWidth>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.mature} onChange={(e) => setForm({ ...form, mature: e.target.checked })} data-testid="channel-mature" />
              <span className="text-xs text-zinc-400">Warn viewers before entering the stream (18+)</span>
            </label>
          </Field>
        </FieldGrid>
      </SectionBlock>

      {/* Bio + panels */}
      <SectionBlock icon={Globe} title="About & Panels" color="pink">
        <Field label="Channel description / bio">
          <textarea className="sc-input min-h-[100px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="channel-description" />
        </Field>
        <Field label="Panels (Markdown)" hint="Shown on your public channel below the stream">
          <textarea className="sc-input min-h-[120px] sc-mono text-xs" value={form.panels_markdown} onChange={(e) => setForm({ ...form, panels_markdown: e.target.value })} placeholder="## About me&#10;Hi, I'm…" data-testid="channel-panels" />
        </Field>
        <Field label="Contest rules" hint="Community giveaways / tournaments">
          <textarea className="sc-input min-h-[80px]" value={form.contest_rules} onChange={(e) => setForm({ ...form, contest_rules: e.target.value })} data-testid="channel-contest" />
        </Field>
      </SectionBlock>

      {/* Schedule */}
      <SectionBlock icon={Calendar} title="Schedule" color="cyan">
        <Field label="Weekly schedule" hint="Displayed on your channel page. Free-form text.">
          <textarea id="schedule" className="sc-input min-h-[100px]" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Mon–Fri 7pm CT · Saturdays subathon · Sun off" data-testid="channel-schedule" />
        </Field>
      </SectionBlock>

      {/* Social links */}
      <SectionBlock icon={Tag} title="Social Links" color="volt">
        <FieldGrid>
          {SOCIAL_KEYS.map((k) => (
            <Field key={k} label={SOCIAL_LABELS[k]}>
              <input
                className="sc-input"
                value={socials[k] || ""}
                onChange={(e) => setSocials({ ...socials, [k]: e.target.value })}
                placeholder="https://…"
                data-testid={`channel-social-${k}`}
              />
            </Field>
          ))}
        </FieldGrid>
      </SectionBlock>

      <SaveBar dirty={!!dirty} onSave={save} onReset={reset} busy={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------
export function SectionBlock({ icon: Icon, title, color = "cyan", children }) {
  return (
    <div className="term-card p-5 space-y-4" data-testid={`block-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: `var(--sc-${color})` }} />
        <span className="sc-label" style={{ color: `var(--sc-${color})` }}>◤ {title.toUpperCase()}</span>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export function FieldGrid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3">{children}</div>;
}

export function Field({ label, hint, children, fullWidth }) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <div className="sc-label mb-1 text-[10px]">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

export function SaveBar({ dirty, onSave, onReset, busy }) {
  if (!dirty) {
    return (
      <div className="text-[10px] sc-mono text-zinc-500" data-testid="channel-save-bar-clean">
        All changes saved.
      </div>
    );
  }
  return (
    <div
      className="sticky bottom-4 z-20 flex items-center justify-between p-3 rounded border border-[color:var(--sc-volt)] bg-[#0F0F14]/95 backdrop-blur"
      data-testid="channel-save-bar"
    >
      <span className="text-xs text-zinc-300 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[color:var(--sc-orange)] animate-pulse" />
        Unsaved changes
      </span>
      <div className="flex gap-2">
        <button type="button" onClick={onReset} className="sc-btn-ghost text-xs" data-testid="channel-reset-btn">
          Cancel
        </button>
        <button type="button" onClick={onSave} disabled={busy} className="sc-btn text-xs flex items-center gap-1.5 disabled:opacity-50" data-testid="channel-save-btn">
          <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}
